import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { KeycloakService } from './keycloak.service';

export type OrderSseEventType =
  | 'created'
  | 'updated'
  | 'cancelled'
  | 'logistics-updated'
  | 'o2o-status-updated'
  | 'modification-pending-approval'
  | 'modification-expired'
  | 'modification-approved'
  | 'modification-rejected';

export interface OrderEvent {
  eventType: OrderSseEventType | string;
  outerOrderNo?: string;
  innerOrderNo?: string;
  businessType?: string;
  source?:
    | 'channel'
    | 'workflow'
    | 'logistics'
    | 'payment'
    | 'fulfillment'
    | 'modification-approval'
    | string;
  /** BRD business status (10-state canonical). */
  orderStatus?: string;
  /** Operational fulfillment machine state (30xxx/31xxx). */
  fulfillmentStatus?: string;
  eventTime?: string;
  orderId?: number;
  modificationRequestId?: number;
  requiredApprovalLevel?: number;
  requestedBy?: string;
  requestedByName?: string;
  changeSummary?: string;
  expiresAt?: string;
  audience?: 'APPROVERS' | 'REQUESTER' | string;
}

/**
 * Service for managing Server-Sent Events (SSE) connection to receive real-time order updates.
 * Automatically reconnects on connection loss with exponential backoff.
 */
@Injectable({
  providedIn: 'root',
})
export class OrderSseService implements OnDestroy {
  private eventSource: EventSourcePolyfill | null = null;
  private readonly orderEvents$ = new Subject<OrderEvent>();
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 10;
  private readonly baseReconnectDelay = 1000;
  private reconnectTimeout: any = null;
  private isConnecting = false;
  private activeConsumers = 0;

  constructor(private readonly keycloakService: KeycloakService) {}

  get orderEvents(): Observable<OrderEvent> {
    return this.orderEvents$.asObservable();
  }

  connect(): void {
    this.activeConsumers++;
    void this.openConnection();
  }

  private async openConnection(): Promise<void> {
    if (this.eventSource || this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    const sseUrl = resolveOrderSseUrl();

    try {
      const token =
        (await this.keycloakService.updateToken(30)) ??
        this.keycloakService.getToken();

      if (!token || this.activeConsumers === 0) {
        this.isConnecting = false;
        return;
      }

      const eventSourceInitDict = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      this.eventSource = new EventSourcePolyfill(sseUrl, eventSourceInitDict);

      this.eventSource.onopen = () => {
        this.reconnectAttempts = 0;
        this.isConnecting = false;
      };

      // Handle all order events with single event name
      this.eventSource.addEventListener('order-event', (event) => {
        const messageEvent = event as MessageEvent<string>;
        let data: any;
        try {
          data = JSON.parse(messageEvent.data);
        } catch (error) {
          console.error('[OrderSseService] Failed to parse SSE event:', error);
          return;
        }
        this.orderEvents$.next({
          eventType: data.eventType,
          outerOrderNo: data.outerOrderNo,
          innerOrderNo: data.innerOrderNo,
          businessType: data.businessType,
          source: data.source,
          orderStatus: data.orderStatus,
          fulfillmentStatus: data.fulfillmentStatus,
          eventTime: data.eventTime,
          orderId: data.orderId,
          modificationRequestId: data.modificationRequestId,
          requiredApprovalLevel: data.requiredApprovalLevel,
          requestedBy: data.requestedBy,
          requestedByName: data.requestedByName,
          changeSummary: data.changeSummary,
          expiresAt: data.expiresAt,
          audience: data.audience,
        });
      });

      this.eventSource.onerror = (error) => {
        this.isConnecting = false;
        this.handleConnectionError();
      };
    } catch (error) {
      console.error('[OrderSseService] Failed to connect SSE stream:', error);
      this.isConnecting = false;
      this.handleConnectionError();
    }
  }

  disconnect(): void {
    if (this.activeConsumers > 0) {
      this.activeConsumers--;
    }

    if (this.activeConsumers > 0) {
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  private handleConnectionError(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    if (
      this.activeConsumers > 0 &&
      this.reconnectAttempts < this.maxReconnectAttempts
    ) {
      const delay =
        this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts);

      this.reconnectTimeout = setTimeout(() => {
        this.reconnectAttempts++;
        void this.openConnection();
      }, delay);
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.orderEvents$.complete();
  }
}

/**
 * When the app talks through back-office-bff, subscribe on the local SSE proxy.
 * Path=/api/order/** is proxied by Gateway MVC RestClient, which buffers the
 * stream so the browser receives no bytes.
 */
function resolveOrderSseUrl(): string {
  const base = environment.orderServiceBasePath.replace(/\/$/, '');
  if (base.endsWith('/api/order')) {
    return `${base.slice(0, -'/api/order'.length)}/_bff/order/v1/order/events/subscribe`;
  }
  return `${base}/v1/order/events/subscribe`;
}
